import React, { useMemo } from 'react'
import type {
  DriverVehicleImportItem,
  ImportsReadModel,
} from '../model/imports.state.types'
import { importsReducer } from '../state/imports.reducer'
import { createInitialFeatureState } from '../state/imports.initialState'
import {
  selectVisibleDriverImportIds,
  selectVisibleVehicleImportIds,
} from '../selectors/imports.selectors'
import { buildSubmitImportsRequest } from '../model/imports.toSubmit'
import { ImportedFileLogsSubmitDto } from '../model/imports.submit.dto'
import { ImportSummarySnapshot } from '../container/import-popup-container/useImportFlow'
import { DriverRow } from './DriverRow'

import { getDisplaySources } from '../pages/home/components/import-review/utils'
import styles from './styles.module.css'
import clx from 'classnames'
import { Button, Checkbox } from '@/ui'
import { faCircleInfo, faChevronDown, faChevronRight, FaIcon } from '@/lib/fa'
import { VehicleRow } from './VehicleRow'
import { VehicleUsedTable } from './VehicleUsedTable'
import { Popup } from '@/ui/overlays/Popup'
import { DriverPicker } from '@/modules/shared/components/driver-picker/DriverPicker'
import { VehiclePicker } from '@/modules/shared/components/vehicle-picker/VehiclePicker'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import {
  faLongArrowRight,
  faTruck,
  faUser,
} from '@fortawesome/free-solid-svg-icons'
import { isDataFile } from '../utils/fileKind'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SelectedSummary } from './step-import-selection/SelectedSummary'
import { SelectDriversAndVehiclesInfoHelper } from './step-import-selection/SelectDriversAndVehiclesInfoHelper'
import { ImportErrorsAccordion } from './ImportErrorsAccordion'
import { FileInfoModal } from './FileInfoModal'
import type { FileDetailsDto } from '@/lib/api/company/fileProcessing/fileDetails/types'
import type { UploadedFileApiDto } from '../model/imports.api.dto'
import { getReadStatus } from '@/app/services/import/readStatusResolver'
import { getCertificateStatus } from '@/app/services/import/certificateStatusResolver'
import { getSource } from '@/app/services/import/sourceResolver'
import { FileType } from '@/app/constants/fileType'

function toIso(v: unknown): string | null {
  if (!v) return null
  if (typeof v === 'string') return v
  if (typeof (v as any).toISOString === 'function') return (v as any).toISOString()
  return String(v)
}

function buildFileDetails(file: UploadedFileApiDto): FileDetailsDto | null {
  if (!file.cardReportDetailsJson) return null
  let raw: any
  try { raw = JSON.parse(file.cardReportDetailsJson) } catch { return null }
  const cd = raw?.cardDetails ?? {}
  const vr = raw?.verifyReport ?? {}
  return {
    source: getSource(file.source),
    readStatus: getReadStatus(file.readStatus),
    certificateStatus: getCertificateStatus(file.certificateStatus),
    cardValidFrom: toIso(file.cardValidFrom),
    cardExpirationDate: toIso(file.cardExpirationDate),
    firstActivityTime: toIso(file.firstActivityTime),
    lastActivityTime: toIso(file.lastActivityTime),
    downloadTime: toIso(file.downloadTime),
    fileName: file.fileName,
    error: null,
    isCertificateValidityDateMismatch: vr.isCertificateValidityDateMismatch ?? false,
    certificateValidityDate: vr.certificateValidityDate ?? null,
    tachographDetails: cd.tachographDetails ?? null,
    companyLocks: cd.companyLocks ?? [],
    calibrations: cd.calibrationDetails ?? [],
    faults: cd.faultDetails ?? [],
    controls: cd.controlDetails ?? [],
    signatures: (vr.signatureInfos ?? []).map((s: any) => ({
      signatureBlockTypeName: s.signatureBlockTypeName ?? null,
      signatureCorrect: s.signatureCorrect ?? true,
      activityDateFormatted: s.activityDateFormatted ?? null,
    })),
    fileStructureWarnings: raw?.fileWarnings ?? [],
    speedInfo: cd.vehicleSpeedInformation ? {
      vehicleRegistrationNumber: cd.vehicleSpeedInformation.vehicleRegistrationNumber ?? null,
      speedPerMinute: (cd.vehicleSpeedInformation.speedPerMinute ?? []).map((p: any) => ({
        minute: p.minute,
        speed: p.speed ?? [],
      })),
    } : null,
  }
}

type InnerProps = {
  companyId: string
  readModel: ImportsReadModel
  handleImport: (
    logs: ImportedFileLogsSubmitDto,
    snapshot: ImportSummarySnapshot,
  ) => void
  handleCancel: () => void
  handleDone: () => void
}

export function PopupStepImportingInner({
  companyId,
  readModel,
  handleImport,
  handleCancel,
  handleDone,
}: InnerProps) {
  const { t } = useLocalize()
  const [state, dispatch] = React.useReducer(importsReducer, readModel, (rm) =>
    createInitialFeatureState(rm),
  )

  // If readModel can change (refetch), re-init
  React.useEffect(() => {
    dispatch({ type: 'INIT_FROM_READ_MODEL', payload: { readModel } })
  }, [readModel])

  const visibleDriverIds = selectVisibleDriverImportIds(state)
  const visibleVehicleIds = selectVisibleVehicleImportIds(state)

  const onSubmit = () => {
    const visibleVehicleIdSet = new Set(filteredVisibleVehicleIds)
    const logs = buildSubmitImportsRequest(state, visibleVehicleIdSet)
    handleImport(logs, {
      selectedDrivers: selectedDriversAmount,
      totalDrivers: visibleDriverIds.length,
      selectedVehicles: selectedVehiclesAmount,
      totalVehicles: filteredVisibleVehicleIds.length,
      filteredVehicleRegistrationNumbers: filteredVisibleVehicleIds
        .map((id) => state.readModel.vehicleItemsById[id]?.registrationNumber)
        .filter(Boolean) as string[],
      selectedVehicleRegistrationNumbers: filteredVisibleVehicleIds
        .filter((id) => state.ui.selectedVehicleImportIds.has(id))
        .map((id) => state.readModel.vehicleItemsById[id]?.registrationNumber)
        .filter(Boolean) as string[],
      selectedDriverCardNumbers: [...state.ui.selectedDriverImportIds]
        .map((id) => state.readModel.driverItemsById[id]?.cardNumber)
        .filter(Boolean) as string[],
    })
  }

  const getFilesForDriverImportId = (driverImportId: string) => {
    const { driverImportIdsByFileId, filesById } = state.readModel

    return Object.entries(driverImportIdsByFileId)
      .filter(([, driverIds]) => driverIds.includes(driverImportId))
      .map(([fileId]) => filesById[fileId])
      .filter(Boolean)
  }

  const getFilesForVehicleImportId = (driverImportId: string) => {
    const { vehicleImportIdsByFileId, filesById } = state.readModel

    return Object.entries(vehicleImportIdsByFileId)
      .filter(([, driverIds]) => driverIds.includes(driverImportId))
      .map(([fileId]) => filesById[fileId])
      .filter(Boolean)
  }

  const rowGridClass = styles.row
  const rowGridVehiclesClass = styles.row

  const allVisibleDriversSelected =
    visibleDriverIds.length > 0 &&
    visibleDriverIds.every((id) => state.ui.selectedDriverImportIds.has(id))

  const allVisibleVehiclesSelected =
    visibleVehicleIds.length > 0 &&
    visibleVehicleIds.every((id) => state.ui.selectedVehicleImportIds.has(id))

  const selectedDriversAmount = state.ui.selectedDriverImportIds.size
  const totalDriversAmount = Object.values(
    state.readModel.driverItemsById,
  ).length
  const selectedVehiclesAmount = state.ui.selectedVehicleImportIds.size
  const totalVehiclesAmount = Object.values(
    state.readModel.vehicleItemsById,
  ).length
  const nothingToSelect = totalDriversAmount === 0 && totalVehiclesAmount === 0
  const dataFiles = Object.values(state.readModel.filesById).filter(isDataFile)
  const hasSavedDataFiles = dataFiles.length > 0
  const savedAnyRows = dataFiles.some((f) => (f.validLines ?? 0) > 0)

  /** ASSIGN */
  const [isAttachDriverPopupVisible, setIsAttachDriverPopupVisible] =
    React.useState(false)
  const [attachTarget, setAttachTarget] = React.useState<{
    driverImportId: string
    label: string
  } | null>(null)
  const openAttachDriverPopup = (driverImportId: string, label: string) => {
    setAttachTarget({ driverImportId, label })
    setIsAttachDriverPopupVisible(true)
  }
  const closeAttachDriverPopup = () => {
    setIsAttachDriverPopupVisible(false)
    setAttachTarget(null)
  }
  const driverAssignedInitial = useMemo(() => {
    try {
      const { driverAssignedToByImportId } = state.ui
      const initial = driverAssignedToByImportId[attachTarget.driverImportId]
      if (initial) {
        return [initial]
      }
    } catch (e) {}
    return []
  }, [attachTarget, state])

  /** ASSIGN VEHICLE */
  const [isAttachVehiclePopupVisible, setIsAttachVehiclePopupVisible] =
    React.useState(false)

  const [attachVehicleTarget, setAttachVehicleTarget] = React.useState<{
    vehicleImportId: string
    label: string
  } | null>(null)

  const openAttachVehiclePopup = (vehicleImportId: string, label: string) => {
    setAttachVehicleTarget({ vehicleImportId, label })
    setIsAttachVehiclePopupVisible(true)
  }

  const closeAttachVehiclePopup = () => {
    setIsAttachVehiclePopupVisible(false)
    setAttachVehicleTarget(null)
  }

  const wrapConfirm = useWrapConfirm()

  const [selectedFileId, setSelectedFileId] = React.useState<string | null>(null)

  /** Expandable sections: DRIVERS and VEHICLES, default collapsed */
  const [driversExpanded, setDriversExpanded] = React.useState(true)
  const [vehiclesExpanded, setVehiclesExpanded] = React.useState(true)

  const vehicleAssignedInitial = useMemo(() => {
    try {
      // adjust name if different
      const { vehicleAssignedToByImportId } = state.ui as any
      const initial =
        vehicleAssignedToByImportId?.[
          attachVehicleTarget?.vehicleImportId as any
        ]
      if (initial) return [initial]
    } catch (e) {}
    return []
  }, [attachVehicleTarget, state])

  // REST

  const filterDrivedUsedVehicles =
    (dateFrom: string) =>
    (vehicle: DriverVehicleImportItem): boolean => {
      if (!dateFrom) return true

      // Hide vehicle if its last use is before dateFrom
      return new Date(vehicle.lastUse) >= new Date(dateFrom)
    }

  // Collect vehicle IDs referenced by drivers and which ones survive dateFrom filtering.
  // Used to hide vehicles from the standalone list when they're filtered out by all drivers.
  const driverReferencedVehicleIds = new Set<string>()
  const survivingVehicleIds = new Set<string>()
  for (const driverId of visibleDriverIds) {
    const item = state.readModel.driverItemsById[driverId]
    const dateFrom = state.ui.driverDateFromByImportId[driverId] ?? ''
    const filterVehicles = filterDrivedUsedVehicles(dateFrom)
    for (const v of item.vehiclesUsed) {
      driverReferencedVehicleIds.add(v.id)
      if (filterVehicles(v)) {
        survivingVehicleIds.add(v.id)
      }
    }
  }

  const filteredVisibleVehicleIds = visibleVehicleIds.filter(
    (id) => !driverReferencedVehicleIds.has(id) || survivingVehicleIds.has(id),
  )

  return (
    <div className="tw-flex tw-h-[calc(100vh-150px)] tw-flex-col tw-rounded-lg">
      {/* Header */}
      <div className="tw-mb-2 tw-flex tw-flex-none tw-items-start tw-justify-between tw-rounded-t-lg">
        <div>
          <h2 className="tw-text-base tw-font-semibold tw-text-slate-900">
            {t('importModule.loading-data-from-file')}
          </h2>
        </div>
      </div>

      <section className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-border tw-border-gray-300 tw-rounded-lg">
        {/* Main content */}
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-gap-6 tw-rounded-lg">
          {/* Left: table */}
          <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-rounded-lg">
            <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-1 tw-flex-col tw-justify-between tw-overflow-y-auto">
              <div>
                <ImportErrorsAccordion
                  filesById={state.readModel.filesById}
                  customLogs={state.readModel.customLogs}
                  onFileDetailsClick={(fileId) => setSelectedFileId(fileId)}
                  defaultCollapsed={
                    visibleDriverIds.length > 0 || filteredVisibleVehicleIds.length > 0
                  }
                />

                {hasSavedDataFiles && (
                  <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-blue-200 tw-bg-blue-50 tw-p-4 tw-text-sm tw-text-blue-900">
                    <FaIcon
                      icon={faCircleInfo}
                      className="tw-mt-0.5 tw-text-blue-500"
                    />

                    <div>
                      <p className="tw-font-medium">{t('importModule.data-files-saved-title')}</p>
                      <p className="tw-mt-1 tw-text-blue-800">
                        {t('importModule.data-files-saved-desc')}
                      </p>
                    </div>
                  </div>
                )}
                  <>
                    {/* DRIVERS – expandable, default collapsed */}
                    {Object.values(state.readModel.driverItemsById).length >
                    0 ? (
                      <div className="tw-border-b tw-border-slate-200">
                        <button
                          type="button"
                          onClick={() => setDriversExpanded((e) => !e)}
                          className={clx(
                            'tw-grid tw-w-full tw-cursor-pointer tw-items-center tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-100',
                            rowGridClass,
                          )}
                        >
                          <div className="tw-flex tw-items-center tw-justify-center">
                            <FaIcon
                              icon={
                                driversExpanded ? faChevronDown : faChevronRight
                              }
                              className="tw-h-4 tw-w-4 tw-text-slate-500"
                            />
                            <FaIcon icon={faUser} className="tw-mx-2" />
                          </div>
                          <div className={styles.titleCol}>
                            {t('importPanel.drivers')} ({visibleDriverIds.length})
                          </div>
                          <div />
                          <div />
                          <div />
                          <div />
                          <div />
                          <div />
                        </button>

                        {driversExpanded && (
                          <>
                            {/* Table header tw-grid-cols-[32px,2.2fr,1.3fr,1.3fr,0.9fr,1.2fr,32px] */}
                            <div
                              className={clx(
                                'tw-grid tw-items-center tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-500',
                                rowGridClass,
                              )}
                            >
                              <div className="tw-flex tw-items-center tw-justify-center">
                                {state ? (
                                  <Checkbox
                                    checked={allVisibleDriversSelected}
                                    onChange={() => {
                                      if (allVisibleDriversSelected) {
                                        dispatch({
                                          type: 'CLEAR_SELECTED_DRIVERS',
                                        })
                                      } else {
                                        dispatch({
                                          type: 'SELECT_ALL_DRIVERS',
                                          payload: {
                                            importIds: visibleDriverIds,
                                          },
                                        })
                                      }
                                    }}
                                  />
                                ) : null}
                              </div>
                              <div className={styles.titleCol}>
                                {t('importModule.col-name')}
                              </div>
                              <div>{t('importModule.col-db-status')}</div>
                              <div>{t('importModule.col-data-source')}</div>
                              <div>{t('importModule.col-assign')}</div>
                              <div>{t('importModule.col-days-count')}</div>
                              <div>{t('importModule.col-read-range')}</div>
                              <div />
                            </div>

                            {/* Rows drivers */}
                            <div className="tw-divide-y tw-divide-slate-100">
                              {visibleDriverIds.map((id) => {
                                const item = state.readModel.driverItemsById[id]
                                const selected =
                                  state.ui.selectedDriverImportIds.has(id)
                                const files = getFilesForDriverImportId(id)
                                const sources = getDisplaySources(files)
                                const dateFrom =
                                  state.ui.driverDateFromByImportId[id] ?? ''
                                const filterVehicles =
                                  filterDrivedUsedVehicles(dateFrom)

                                const vehicles = item.vehiclesUsed
                                  .filter(filterVehicles)
                                  .map((v) => {
                                    const vehicleId = v.id
                                    const vehicle =
                                      vehicleId in
                                      state.readModel.vehicleItemsById
                                        ? state.readModel.vehicleItemsById[
                                            vehicleId
                                          ]
                                        : null

                                    // Clip firstUse to dateFrom (WPF CutToDate behavior)
                                    const clippedFirstUse =
                                      dateFrom &&
                                      new Date(v.firstUse) < new Date(dateFrom)
                                        ? dateFrom
                                        : v.firstUse

                                    return {
                                      usedVehicle: {
                                        ...v,
                                        firstUse: clippedFirstUse,
                                      },
                                      sources: getDisplaySources(files),
                                      vehicle,
                                      originalFirstUse:
                                        clippedFirstUse !== v.firstUse
                                          ? v.firstUse
                                          : undefined,
                                    }
                                  })

                                // activityDates = new card days not in DB (StartTime.Date only, no restriction).
                                // No date = show all new days. Date set = show new days from that date.
                                // Empty [] must not win over daysRead: `0 ?? daysRead` is 0.
                                const activityDayCount = !dateFrom
                                  ? (item.activityDates?.length ?? 0)
                                  : (item.activityDates?.filter((d) => d >= dateFrom)
                                      .length ?? 0)
                                const effectiveDays =
                                  activityDayCount > 0
                                    ? activityDayCount
                                    : dateFrom && vehicles.length === 0
                                      ? 0
                                      : item.daysRead

                                return (
                                  <DriverRow
                                    key={id}
                                    checked={selected}
                                    name={item.fullName}
                                    id={item.cardNumber}
                                    status={item.bucket}
                                    days={effectiveDays}
                                    sources={sources}
                                    onCheck={() => {
                                      dispatch({
                                        type: 'TOGGLE_DRIVER_SELECTED',
                                        payload: { importId: id },
                                      })
                                    }}
                                    className={rowGridClass}
                                    assigned={Boolean(
                                      state.ui.driverAssignedToByImportId[id],
                                    )}
                                    handleAssign={() => {
                                      openAttachDriverPopup(
                                        id,
                                        `${item.fullName} (ID: ${item.cardNumber})`,
                                      )
                                    }}
                                    dateFrom={
                                      state.ui.driverDateFromByImportId[id] ??
                                      ''
                                    }
                                    handleDateFrom={(dateFrom: string) => {
                                      dispatch({
                                        type: 'SET_DRIVER_DATE_FROM',
                                        payload: { importId: id, dateFrom },
                                      })
                                    }}
                                  >
                                    <VehicleUsedTable
                                      vehicles={vehicles}
                                      className=""
                                    />
                                  </DriverRow>
                                )
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}

                    <div className="tw-border-t tw-border-slate-200" />

                    {/* VEHICLES – expandable, default collapsed */}
                    {filteredVisibleVehicleIds.length > 0 ? (
                      <div className="tw-border-b tw-border-slate-200">
                        <button
                          type="button"
                          onClick={() => setVehiclesExpanded((e) => !e)}
                          className={clx(
                            'tw-grid tw-w-full tw-cursor-pointer tw-items-center tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-100',
                            rowGridVehiclesClass,
                          )}
                        >
                          <div className="tw-flex tw-items-center tw-justify-center">
                            <FaIcon
                              icon={
                                vehiclesExpanded
                                  ? faChevronDown
                                  : faChevronRight
                              }
                              className="tw-h-4 tw-w-4 tw-text-slate-500"
                            />
                            <FaIcon icon={faTruck} className="tw-mx-2" />
                          </div>
                          <div className={styles.titleCol}>
                            {t('importPanel.vehicles')} ({filteredVisibleVehicleIds.length})
                          </div>
                          <div />
                          <div />
                          <div />
                          <div />
                          <div />
                        </button>

                        {vehiclesExpanded && (
                          <>
                            {/* Table header tw-grid-cols-[32px,2.2fr,1.3fr,1.3fr,0.9fr,1.2fr,32px] */}
                            <div
                              className={clx(
                                'tw-grid tw-items-center tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-slate-500',
                                rowGridVehiclesClass,
                              )}
                            >
                              <div className="tw-flex tw-items-center tw-justify-center">
                                {state ? (
                                  <Checkbox
                                    checked={allVisibleVehiclesSelected}
                                    onChange={() => {
                                      if (allVisibleVehiclesSelected) {
                                        dispatch({
                                          type: 'CLEAR_SELECTED_VEHICLES',
                                        })
                                      } else {
                                        dispatch({
                                          type: 'SELECT_ALL_VEHICLES',
                                          payload: {
                                            importIds: visibleVehicleIds,
                                          },
                                        })
                                      }
                                    }}
                                  />
                                ) : null}
                              </div>
                              <div className={styles.titleCol}>{t('importModule.col-registration')}</div>
                              <div>{t('importModule.col-db-status')}</div>
                              <div>{t('importModule.col-data-source')}</div>
                              <div>{t('importModule.col-assign')}</div>
                              <div>{/* ZAKRES WCZYTYWANIA */}</div>
                            </div>

                            {/* Temporary Rows vehicles */}
                            <div className="tw-divide-y tw-divide-slate-100">
                              {filteredVisibleVehicleIds.map((id) => {
                                const item =
                                  state.readModel.vehicleItemsById[id]
                                const selected =
                                  state.ui.selectedVehicleImportIds.has(id)
                                const files = getFilesForVehicleImportId(id)
                                const sources = getDisplaySources(files)

                                return (
                                  <VehicleRow
                                    key={id}
                                    checked={selected}
                                    onCheck={() => {
                                      dispatch({
                                        type: 'TOGGLE_VEHICLE_SELECTED',
                                        payload: { importId: id },
                                      })
                                    }}
                                    registrationNumber={item.registrationNumber}
                                    status={item.bucket}
                                    // days={item.daysRead}
                                    sources={sources}
                                    className={rowGridVehiclesClass}
                                    assigned={Boolean(
                                      state.ui.vehicleAssignedToByImportId[id],
                                    )}
                                    handleAssign={() => {
                                      openAttachVehiclePopup(
                                        id,
                                        `${item.registrationNumber}`,
                                      )
                                    }}
                                  />
                                )
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}

                    {/* Bottom helper row */}
                  </>
              </div>
                {!nothingToSelect && <SelectDriversAndVehiclesInfoHelper />}
            </div>

            {/* Summary row */}
            <div className="tw-flex tw-flex-none tw-items-center tw-justify-between tw-border-t tw-border-slate-200 tw-rounded-b-lg tw-px-4 tw-py-3">
              {!nothingToSelect ? (
                <SelectedSummary
                  selectedDriversAmount={selectedDriversAmount}
                  totalDriversAmount={totalDriversAmount}
                  selectedVehiclesAmount={selectedVehiclesAmount}
                  totalVehiclesAmount={totalVehiclesAmount}
                />
              ) : (
                <div />
              )}

              {nothingToSelect ? (
                <Button
                  variant="primary"
                  text={t('common.close')}
                  size="sm"
                  onClick={savedAnyRows ? handleDone : handleCancel}
                  className="tw-h-[38px]"
                />
              ) : (
                <div className="tw-flex tw-gap-1">
                  <Button
                    variant="error"
                    text={t('importModule.abort')}
                    size="sm"
                    onClick={wrapConfirm(handleCancel, {
                      title: t('importModule.confirm-abort-title'),
                      message: t('importModule.confirm-abort-message'),
                    })}
                    className="tw-h-[38px]"
                  />
                  <Button
                    variant="primary"
                    text={t('importModule.import-action')}
                    size="sm"
                    faTrailingIcon={faLongArrowRight}
                    onClick={onSubmit}
                    disabled={
                      selectedDriversAmount < 1 && selectedVehiclesAmount < 1
                    }
                    className="tw-h-[38px]"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Popup
        open={isAttachDriverPopupVisible}
        onClose={closeAttachDriverPopup}
        contentClassName="tw-z-20"
      >
        <DriverPicker
          multiple={false}
          initial={driverAssignedInitial}
          onCancel={closeAttachDriverPopup}
          onSubmit={(selected) => {
            dispatch({
              type: 'SET_DRIVER_ASSIGNED_TO',
              payload: {
                importId: attachTarget.driverImportId,
                assignedTo: selected[0],
              },
            })
            closeAttachDriverPopup()
          }}
          companyId={companyId}
          title={t('importModule.assign-to-existing-driver')}
          subtitleLeft={t('importModule.assigning')}
          subtitleEntity={attachTarget ? attachTarget.label : ''}
          subtitleRight={t('importModule.with-driver-from-db')}
          submitLabel={t('importModule.assign')}
          cancelLabel={t('common.cancel')}
        />
      </Popup>

      <Popup
        open={isAttachVehiclePopupVisible}
        onClose={closeAttachVehiclePopup}
        zIndexClassName="tw-z-20"
      >
        <VehiclePicker
          multiple={false}
          initial={vehicleAssignedInitial}
          onCancel={closeAttachVehiclePopup}
          onSubmit={(selected) => {
            console.log('S', selected)
            dispatch({
              type: 'SET_VEHICLE_ASSIGNED_TO',
              payload: {
                importId: attachVehicleTarget!.vehicleImportId,
                assignedTo: selected[0],
              },
            })
            closeAttachVehiclePopup()
          }}
          companyId={companyId}
          title={t('importModule.assign-to-existing-vehicle')}
          subtitleLeft={t('importModule.assigning')}
          subtitleEntity={attachVehicleTarget ? attachVehicleTarget.label : ''}
          subtitleRight={t('importModule.with-vehicle-from-db')}
          submitLabel={t('importModule.assign')}
          cancelLabel={t('common.cancel')}
        />
      </Popup>

      {selectedFileId && (() => {
        const file = state.readModel.filesById[selectedFileId]
        if (!file || file.fileType !== FileType.Ddd) return null
        return (
          <FileInfoModal
            fileName={file.fileName}
            details={buildFileDetails(file)}
            onClose={() => setSelectedFileId(null)}
          />
        )
      })()}
    </div>
  )
}
